import React, { useContext } from 'react';
import { Box, Chip, colors } from '@material-ui/core';
import { useSearchParams } from 'react-router-dom';
import UserContext from 'state/UserContext';
import AdminGridItems, { MetricStrip } from './RoleBasedGrids/AdminGridItems';
import PMGridItems from './RoleBasedGrids/PMGridItems';

/**
 * Tab values for the admin dashboard. Project managers and supervisors get
 * the same three tabs as admins; technicians and limited users keep a single
 * untabbed page (handled by their own grid components, not here).
 */
export type DashboardView = 'overview' | 'workload' | 'clients';

const DASHBOARD_TABS: { value: DashboardView; label: string }[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'workload', label: 'Workload & Health' },
  { value: 'clients', label: 'Clients' },
];

const isDashboardView = (value: string | null): value is DashboardView =>
  value === 'overview' || value === 'workload' || value === 'clients';

/**
 * Pill tab bar for the dashboard, styled like the job list's category chips
 * (Open / All / Closed). It sits below the metric strip — the cards are fixed
 * across tabs — and above the tab content. URL state lives in `?view=` (a
 * search param, not a path segment) because the dashboard owns `/` and a
 * `/:tab` route would swallow `/jobs` and friends. Only the active tab
 * mounts — React Query fetches on mount, so mounting all three triples
 * dashboard load.
 */
const DashboardTabs = ({ data }: { data: any }) => {
  const { user } = useContext(UserContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const isAdmin = user.role === 'Administrator';

  const tabs =
    isAdmin || user.role === 'ProjectManager' || user.role === 'Supervisor'
      ? DASHBOARD_TABS
      : [];

  // A single untabbed page renders the grid directly with no chrome.
  if (!tabs.length) {
    return null;
  }

  const param = searchParams.get('view');
  const activeView: DashboardView = isDashboardView(param) ? param : 'overview';

  const handleTabChange = (value: DashboardView) => {
    const next = new URLSearchParams(searchParams);
    next.set('view', value);
    setSearchParams(next, { replace: true });
  };

  return (
    <>
      {/* The metric strip is fixed across tabs, so it stays above them. */}
      {isAdmin && (
        <>
          <MetricStrip data={data} />
          <Box mt={2.5} />
        </>
      )}
      <Box style={{ display: 'flex', flexWrap: 'wrap', marginLeft: -5 }}>
        {tabs.map((tab) => {
          const isActive = activeView === tab.value;
          return (
            <Chip
              key={tab.value}
              label={tab.label}
              style={{
                fontWeight: 'bold',
                marginInline: 5,
                color: isActive ? 'white' : 'black',
                backgroundColor: isActive
                  ? colors.blue[800]
                  : 'lightGray',
                border: 'none',
              }}
              onClick={() => handleTabChange(tab.value)}
            />
          );
        })}
      </Box>
      <Box mt={3}>
        {isAdmin ? (
          <AdminGridItems view={activeView} />
        ) : (
          <PMGridItems view={activeView} />
        )}
      </Box>
    </>
  );
};

export default DashboardTabs;
